import React from "react";
import { useTranslation } from "react-i18next";
import { Body1, Caption1, Dot, Link, ListSeparator, Paper } from "@activecollab/components";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import FormatDate from "~/components/Formatters/FormatDate";
import NewFeatureItem from "./NewFeatureItem";
import NewFeatureSkeleton from "./NewFeatureSkeleton";

const NewFeaturesList = ({ newFeatures, loading }) => {
  const { t } = useTranslation();
  const seenFeatures = newFeatures?.filter((feature) => feature.is_seen);
  const unseenFeatures = newFeatures?.filter((feature) => !feature.is_seen);
  return (
    <Paper className="tw-py-6 tw-px-4 tw-flex tw-flex-col tw-gap-4 tw-h-full tw-mb-8">
      {loading ? (
        <div
          data-testid="new-feature-skeleton"
          className="tw-flex tw-flex-col tw-gap-4 tw-w-full"
        >
          {[0, 1, 2, 3, 4, 5].map((_, index) => (
            <NewFeatureSkeleton key={index} />
          ))}
        </div>
      ) : (
        <>
          {unseenFeatures.length > 0 ? (
            unseenFeatures.map((feature, index) => (
              <NewFeatureItem
                key={`${feature.title}-${index}`}
                feature={feature}
              />
            ))
          ) : (
            <div
              data-testid="up-to-date"
              className="tw-flex tw-py-2"
            >
              <div className="tw-flex tw-items-center tw-justify-end sm:tw-w-32 tw-gap-3 tw-h-6 tw-px-3 tw-shrink-0">
                <Caption1
                  letterSpacing="regular"
                  color="secondary"
                >
                  <FormatDate value={moment().unix()} />
                </Caption1>
                <Dot
                  size={4}
                  color="#89898E"
                />
              </div>
              <Body1 weight="bold">
                {t("You are up-to-date with everything. In the meantime,")}
                &nbsp;
                <Link
                  variant="text link-primary"
                  className="tw-no-underline tw-font-bold tw-text-base tw-outline-none tw-inline"
                  href="https://activecollab.com/roadmap"
                  target="_blank"
                >
                  {t("check out what we’re working on next.")}
                </Link>
              </Body1>
            </div>
          )}
          <ListSeparator className="tw-my-0" />
          {seenFeatures.length > 0
            ? seenFeatures.map((feature, index) => (
                <NewFeatureItem
                  key={`${feature.title}-${index}`}
                  feature={feature}
                />
              ))
            : null}
        </>
      )}
    </Paper>
  );
};

NewFeaturesList.propTypes = {
  newFeatures: PropTypes.array,
  loading: PropTypes.bool,
};

NewFeaturesList.displayName = "NewFeaturesList";

export default NewFeaturesList;
